import DropDownButton from 'devextreme-react/drop-down-button';
import { useMemo } from 'react';
import type { CalendarTarget } from '../../../../api/timeOff';
import { countActiveCalendarFilters } from '../timeOffCalendarFilterSummary';
import type { CalendarEmployeeFilterValue } from './TimeOffCalendarEmployeeFilter';
import type { TimeOffCalendarOrgFilter } from './TimeOffCalendarOrgFilter';

interface MenuRow {
  id: string;
  text: string;
  icon?: string;
  disabled?: boolean;
}

interface TimeOffCalendarMobileMenuProps {
  showPast: boolean;
  onShowPastChange: (value: boolean) => void;
  showCancelled: boolean;
  onShowCancelledChange: (value: boolean) => void;
  canAddTimeOff: boolean;
  onAdd: () => void;
  addDisabled?: boolean;
  onMultiAdd: () => void;
  showCalendarFilters: boolean;
  calendarTargets: CalendarTarget[];
  activeTargets: Set<string>;
  onToggleTarget: (id: string) => void;
  employeeFilter: CalendarEmployeeFilterValue | null;
  orgFilter: TimeOffCalendarOrgFilter;
  onResetFilters: () => void;
}

export function TimeOffCalendarMobileMenu({
  showPast,
  onShowPastChange,
  showCancelled,
  onShowCancelledChange,
  canAddTimeOff,
  onAdd,
  addDisabled,
  onMultiAdd,
  showCalendarFilters,
  calendarTargets,
  activeTargets,
  onToggleTarget,
  employeeFilter,
  orgFilter,
  onResetFilters
}: TimeOffCalendarMobileMenuProps) {
  const hasActiveFilters = countActiveCalendarFilters(employeeFilter, orgFilter) > 0;

  const { rows, handlers } = useMemo(() => {
    const menuRows: MenuRow[] = [];
    const menuHandlers = new Map<string, () => void>();

    const push = (id: string, text: string, onClick: () => void, icon?: string, disabled?: boolean) => {
      menuRows.push({ id, text, icon, disabled });
      menuHandlers.set(id, onClick);
    };

    if (canAddTimeOff) {
      push('add', 'Add time off', onAdd, 'plus', addDisabled);
      push('multi-add', 'Multi-Add', onMultiAdd, 'event');
    }

    push(
      'toggle-past',
      showPast ? 'Hide past PTO' : 'Show past PTO',
      () => onShowPastChange(!showPast),
      'clock'
    );
    push(
      'toggle-cancelled',
      showCancelled ? 'Hide cancelled' : 'Show cancelled',
      () => onShowCancelledChange(!showCancelled),
      'check'
    );

    if (showCalendarFilters && calendarTargets.length > 0) {
      for (const target of calendarTargets) {
        const active = activeTargets.has(target.id);
        push(
          `target-${target.id}`,
          `${active ? 'Hide' : 'Show'} ${target.displayName}`,
          () => onToggleTarget(target.id),
          active ? 'check' : 'isblank'
        );
      }
    }

    if (hasActiveFilters) {
      push('reset-filters', 'Reset filters', onResetFilters, 'refresh');
    }

    return { rows: menuRows, handlers: menuHandlers };
  }, [
    showPast,
    onShowPastChange,
    showCancelled,
    onShowCancelledChange,
    canAddTimeOff,
    onAdd,
    addDisabled,
    onMultiAdd,
    showCalendarFilters,
    calendarTargets,
    activeTargets,
    onToggleTarget,
    hasActiveFilters,
    onResetFilters
  ]);

  return (
    <DropDownButton
      className="time-off-calendar-mobile-menu-btn"
      icon="menu"
      stylingMode="outlined"
      hint="Calendar options"
      displayExpr="text"
      keyExpr="id"
      dropDownOptions={{ width: 260, showTitle: false }}
      items={rows}
      onItemClick={(e) => {
        const id = e.itemData?.id;
        if (typeof id === 'string') handlers.get(id)?.();
      }}
    />
  );
}
